import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface CreditBalance {
  creditBalance: number;
  lifetimeCreditsUsed: number;
  lifetimeCreditsPurchased: number;
  freeChaptersUsed: number;
}

interface Transaction {
  id: string;
  type: string;
  creditAmount: number;
  creditBalanceAfter: number;
  description: string;
  relatedBookId: string | null;
  createdAt: unknown;
}

export function useCredits() {
  // MEDIUM-2 FIX: Only fire the query when authenticated to avoid 401 errors
  // on public pages (landing, login, pricing).
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<CreditBalance>>('/credits/balance');
      // Return a zero-balance default when no credits doc exists (free users).
      // RQ v5 throws if queryFn returns undefined.
      return res.data.data ?? {
        creditBalance: 0,
        lifetimeCreditsUsed: 0,
        lifetimeCreditsPurchased: 0,
        freeChaptersUsed: 0,
      };
    },
    enabled: !!user,
  });
}

export function useTransactions() {
  return useQuery({
    queryKey: ['credits', 'transactions'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ transactions: Transaction[] }>>('/credits/transactions');
      return res.data.data?.transactions ?? [];
    },
  });
}

export function usePurchaseCredits() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiSuccess<{ checkoutUrl: string }>>('/credits/purchase');
      return res.data.data;
    },
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
  });
}
